import ufeApi from 'services/api/ufeApi';
import getAuthDataId from 'services/api/utility/getAuthDataId';
import userUtils from 'utils/User';

/**
 * Fetches GWP (Gift With Purchase) promotion items from Product Aggregation Service (PAS)
 * Used to retrieve alternate GWP options when original selection is out of stock
 *
 * @param {Object} options - Configuration options
 * @param {string[]} options.contextIds - Array of contentful context IDs to query PAS
 * @param {string} [options.defaultSAZipCode] - Store zipcode for inventory availability
 * @param {string} [options.defaultSACountryCode] - Store country code for inventory availability
 * @returns {Promise<Object>} PAS response with GWP promotion data
 * @throws {Error} When API returns error or network failure
 */
function getGWPPromotions(options = {}) {
    const { contextIds = [], defaultSAZipCode, defaultSACountryCode } = options;

    if (!contextIds || contextIds.length === 0) {
        return Promise.reject(new Error('Context IDs are required to fetch GWP promotions'));
    }

    // Get current location and channel info
    const { country, channel, language } = Sephora.renderQueryParams;
    const biId = getAuthDataId();

    // Build PAS URL with query parameters
    const params = new URLSearchParams({
        context: contextIds.join(','),
        biId,
        ch: channel,
        loc: `${language}-${country}`
    });

    const url = `/gway/productaggregation/aggregate?${params}`;

    // Build headers with store location for inventory availability
    const headers = {
        defaultSACountryCode: defaultSACountryCode || userUtils.getDefaultSACountryCode(),
        defaultSAZipCode: defaultSAZipCode || userUtils.getDefaultSAZipCode()
    };

    return ufeApi
        .makeRequest(url, {
            method: 'GET',
            headers
        })
        .then(data => {
            // Check for error responses
            if (data.errorCode || (data.responseStatus && data.responseStatus !== 200)) {
                Sephora.logger.error('Get GWP promotions API error', {
                    errorCode: data.errorCode || data.responseStatus,
                    url
                });

                return Promise.reject(data);
            }

            return data;
        })
        .catch(error => {
            Sephora.logger.error('Get GWP promotions error', {
                error,
                url,
                message: error?.message || 'Unknown error'
            });

            return Promise.reject(error);
        });
}

export default getGWPPromotions;
